/**
 * PATH: packages/ui/src/kanban/components/SpeechMicButton.tsx
 * FILE_ID: UI-KANBAN-SPEECH-001
 * PURPOSE: Inline microphone button for dictating into a parent text input.
 *          Hybrid engine: Web Speech API where it actually works (Chrome /
 *          Safari give live interim text), with automatic fallback to
 *          server-side transcription (MediaRecorder -> /api/ai/transcribe/)
 *          everywhere else.
 * WHY:
 *   Operator complaint 2026-05-08: "There should also be a speech to audio
 *   function for description / notes." Then 2026-06-15: "why is recording not
 *   working". Root cause: the button was Web-Speech-only, and
 *   `webkitSpeechRecognition` only transcribes in official Google Chrome /
 *   Safari — in any other Chromium build (Brave, Electron, embedded browsers)
 *   it fails instantly with `error:network` and the old code hid that behind a
 *   silent mic-off icon, so it looked dead.
 *
 *   Fix: when Web Speech errors with `network`/`service-not-allowed` (or isn't
 *   available), we remember that for the session and transparently fall back to
 *   recording audio and transcribing it on the server, which works in every
 *   browser. If neither engine is available we render nothing.
 *
 * FLOW:
 *   click ─▶ Web Speech (live)            ─▶ onAppendText (interim+final)
 *           │ error:network / unavailable
 *           ▼
 *           MediaRecorder ─▶ /api/ai/transcribe/ ─▶ onAppendText (final)
 *
 * DEPENDENCIES:
 *   - ../hooks/useBackendRecorder: getUserMedia + MediaRecorder capture
 *   - KanbanApi.transcribeAudio (injected via `transcribe` prop)
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Mic, MicOff } from 'lucide-react'

import { useBackendRecorder } from '../hooks/useBackendRecorder'
import { ensureMicPermission } from '../../speech/ensureMicPermission'

interface SpeechRecognitionAlternativeLite { transcript: string }
interface SpeechRecognitionResultLite {
  0: SpeechRecognitionAlternativeLite
  isFinal: boolean
  length: number
}
interface SpeechRecognitionEventLite {
  resultIndex: number
  results: { [i: number]: SpeechRecognitionResultLite; length: number }
}
interface SpeechRecognitionLite {
  lang: string
  continuous: boolean
  interimResults: boolean
  start: () => void
  stop: () => void
  abort: () => void
  onresult: ((e: SpeechRecognitionEventLite) => void) | null
  onerror: ((e: { error: string }) => void) | null
  onend: (() => void) | null
}

function getSpeechRecognitionCtor(): (new () => SpeechRecognitionLite) | null {
  if (typeof window === 'undefined') return null
  const w = window as unknown as {
    SpeechRecognition?: new () => SpeechRecognitionLite
    webkitSpeechRecognition?: new () => SpeechRecognitionLite
  }
  return w.SpeechRecognition || w.webkitSpeechRecognition || null
}

// Session-scoped memory that Web Speech is broken in THIS browser, so after the
// first `network` failure we skip straight to the backend path on every later
// click instead of re-triggering the broken engine. Module-level mirror avoids
// a sessionStorage read on every render.
const WEBSPEECH_BROKEN_KEY = 'qt:dictation:webspeech-broken'
let webSpeechBrokenMemo: boolean | null = null
function isWebSpeechBroken(): boolean {
  if (webSpeechBrokenMemo !== null) return webSpeechBrokenMemo
  try {
    webSpeechBrokenMemo = sessionStorage.getItem(WEBSPEECH_BROKEN_KEY) === '1'
  } catch { webSpeechBrokenMemo = false }
  return webSpeechBrokenMemo
}
function markWebSpeechBroken() {
  webSpeechBrokenMemo = true
  try { sessionStorage.setItem(WEBSPEECH_BROKEN_KEY, '1') } catch { /* ignore */ }
}

function isCapacitorShell(): boolean {
  if (typeof window === 'undefined') return false
  const cap = (window as unknown as { Capacitor?: { isNativePlatform?: () => boolean } }).Capacitor
  try {
    return !!cap?.isNativePlatform?.()
  } catch {
    return false
  }
}

const ERROR_LABEL: Record<string, string> = {
  mic_permission_denied: 'Microphone permission denied — enable it in your browser',
  'not-allowed': 'Microphone permission denied — enable it in your browser',
  recorder_unsupported: 'Dictation is not supported in this browser',
  transcription_unconfigured: 'Dictation is not configured on the server',
  no_engine: 'Dictation needs Google Chrome or Safari',
}
const CAPACITOR_MIC_DENIED =
  'Microphone permission denied — enable it in your device Settings'
function labelForError(code: string): string {
  if (
    isCapacitorShell() &&
    (code === 'mic_permission_denied' || code === 'not-allowed')
  ) {
    return CAPACITOR_MIC_DENIED
  }
  return ERROR_LABEL[code] || 'Dictation failed — tap to try again'
}

export interface SpeechMicButtonProps {
  onAppendText: (textChunk: string, isFinal: boolean) => void
  /** Host transcription function (KanbanApi.transcribeAudio). When provided,
   *  enables the browser-independent fallback (and the only engine in
   *  non-Chrome browsers). */
  transcribe?: (
    blob: Blob,
    opts?: { language?: string; prompt?: string; filename?: string },
  ) => Promise<string>
  lang?: string
  size?: 'sm' | 'md'
  title?: string
  className?: string
  /** Fires when dictation/transcription busy state flips (for submit guards). */
  onRecordingChange?: (active: boolean) => void
}

export function SpeechMicButton({
  onAppendText,
  transcribe,
  lang,
  size = 'sm',
  title,
  className,
  onRecordingChange,
}: SpeechMicButtonProps) {
  const SR = getSpeechRecognitionCtor()
  const recRef = useRef<SpeechRecognitionLite | null>(null)
  const [webListening, setWebListening] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const interimBufferRef = useRef<string>('')

  const resolvedLang =
    lang || (typeof navigator !== 'undefined' ? navigator.language : '') || 'en-AU'

  const recorder = useBackendRecorder({
    // Safe stub when no host transcribe is wired — `start` guards on
    // isSupported + we never call it without `transcribe` below.
    transcribe: transcribe || (async () => ''),
    language: resolvedLang.split('-')[0],
    onText: (text) => { if (text) onAppendText(text.trim() + ' ', true) },
    onError: (code) => setError(code),
  })

  const backendAvailable = !!transcribe && recorder.isSupported

  const stopWeb = useCallback(() => {
    if (recRef.current) {
      try { recRef.current.stop() } catch { /* already stopped */ }
    }
    setWebListening(false)
  }, [])

  const startBackend = useCallback(() => {
    setError(null)
    void recorder.start()
  }, [recorder])

  const startWeb = useCallback(() => {
    void (async () => {
      if (!SR) { startBackend(); return }
      const perm = await ensureMicPermission()
      if (perm === 'denied') {
        setError('mic_permission_denied')
        return
      }
      setError(null)
      interimBufferRef.current = ''
      let rec: SpeechRecognitionLite
      try {
        rec = new SR()
      } catch {
        markWebSpeechBroken()
        if (backendAvailable) startBackend()
        else setError('no_engine')
        return
      }
      rec.lang = resolvedLang
      rec.continuous = true
      rec.interimResults = true
      rec.onresult = (event) => {
        let interim = ''
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const r = event.results[i]
          if (r.isFinal) {
            onAppendText(r[0].transcript + ' ', true)
            interimBufferRef.current = ''
          } else {
            interim += r[0].transcript
          }
        }
        if (interim && interim !== interimBufferRef.current) {
          interimBufferRef.current = interim
          onAppendText(interim, false)
        }
      }
      rec.onerror = (e) => {
        const msg = String(e.error || 'unknown')
        if (msg === 'no-speech' || msg === 'aborted') return
        // `network` / `service-not-allowed` => this Chromium build can't reach
        // the Google speech backend. Remember it and fall back to the server.
        if (msg === 'network' || msg === 'service-not-allowed') {
          markWebSpeechBroken()
          setWebListening(false)
          recRef.current = null
          if (backendAvailable) { startBackend(); return }
          setError('no_engine')
          return
        }
        setError(msg)
        setWebListening(false)
      }
      rec.onend = () => {
        if (recRef.current === rec) {
          recRef.current = null
          setWebListening(false)
        }
      }
      recRef.current = rec
      try {
        rec.start()
        setWebListening(true)
      } catch {
        markWebSpeechBroken()
        if (backendAvailable) startBackend()
        else setError('no_engine')
      }
    })()
  }, [SR, resolvedLang, onAppendText, backendAvailable, startBackend])

  const busy = webListening || recorder.phase !== 'idle'

  useEffect(() => {
    onRecordingChange?.(busy)
    return () => {
      // Unmount while busy must clear parent submit guards.
      if (busy) onRecordingChange?.(false)
    }
  }, [busy, onRecordingChange])

  const handleClick = useCallback(() => {
    if (busy) {
      if (webListening) stopWeb()
      if (recorder.phase === 'recording') recorder.stop()
      return
    }
    // Prefer Web Speech (live interim text) unless we already know it's broken
    // in this browser; otherwise go straight to the server-side recorder.
    if (SR && !isWebSpeechBroken()) startWeb()
    else if (backendAvailable) startBackend()
    else setError('no_engine')
  }, [busy, webListening, stopWeb, recorder, SR, startWeb, backendAvailable, startBackend])

  useEffect(() => () => stopWeb(), [stopWeb])

  // Render nothing only when there is genuinely no usable engine — Web Speech
  // ctor absent AND no backend fallback wired.
  if (!SR && !backendAvailable) return null

  // md = finger-friendly hit target (Diary / mobile); sm keeps Kanban density.
  const dim = size === 'md' ? 'h-5 w-5' : 'h-3.5 w-3.5'
  const pad = size === 'md' ? 'p-2' : 'p-1'

  const transcribing = recorder.phase === 'transcribing'
  const capturing = webListening || recorder.phase === 'recording'
  const offline =
    typeof navigator !== 'undefined' && navigator.onLine === false

  // The failure reason outranks the host's title. Every host passes a title,
  // so letting it win hid "permission denied" behind a silent mic-off icon.
  const computedTitle = error
    ? labelForError(error)
    : title ||
      (offline && !capturing && !transcribing
        ? 'You look offline — dictation needs a connection for transcription'
        : transcribing ? 'Transcribing…'
          : capturing ? 'Stop dictation'
            : 'Dictate (speech-to-text)')

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={transcribing}
      title={computedTitle}
      aria-pressed={capturing}
      aria-label={capturing ? 'Stop speech-to-text' : 'Start speech-to-text'}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded transition-colors',
        pad,
        capturing
          ? 'bg-red-500 text-white shadow-sm hover:bg-red-600 animate-pulse'
          : transcribing
            ? 'text-blue-500'
            : 'text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 dark:text-zinc-400 dark:hover:bg-zinc-800 dark:hover:text-zinc-100',
        className || '',
      ].join(' ')}
    >
      {capturing || transcribing
        ? <Loader2 className={`${dim} animate-spin`} aria-hidden />
        : error
          ? <MicOff className={dim} aria-hidden />
          : <Mic className={dim} aria-hidden />}
    </button>
  )
}
